import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useApp } from "../lib/store";
import { X, Compass } from "lucide-react";

const STEPS = [
  { key: "welcome", placement: "center" },
  { key: "nav", sel: '[data-testid="sidebar"]', placement: "right", pad: 4 },
  { key: "search", sel: '[data-testid="global-search-input"]', placement: "bottom", pad: 6 },
  { key: "new", sel: '[data-testid="global-new-btn"]', placement: "bottom-end", pad: 6 },
  { key: "notif", sel: '[data-testid="notification-bell"]', placement: "bottom-end", pad: 8 },
  { key: "done", placement: "center" },
];

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export function Tour() {
  const { t, user } = useApp();
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);

  const steps = STEPS.filter((s) => !s.sel || document.querySelector(s.sel));
  const step = steps[i];

  const start = useCallback(() => { setI(0); setOpen(true); }, []);

  useEffect(() => {
    if (user && !localStorage.getItem("lf_tour_seen")) {
      const tmr = setTimeout(() => setOpen(true), 900);
      return () => clearTimeout(tmr);
    }
  }, [user]);

  useEffect(() => {
    const h = () => start();
    window.addEventListener("lf-start-tour", h);
    return () => window.removeEventListener("lf-start-tour", h);
  }, [start]);

  useEffect(() => {
    if (!open || !step) return;
    const update = () => {
      if (step.sel) {
        const el = document.querySelector(step.sel);
        setRect(el ? el.getBoundingClientRect() : null);
      } else setRect(null);
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, i, step]);

  if (!open || !step) return null;

  const finish = () => { localStorage.setItem("lf_tour_seen", "1"); setOpen(false); };
  const next = () => (i < steps.length - 1 ? setI(i + 1) : finish());
  const back = () => setI(Math.max(0, i - 1));

  const pad = step.pad ?? 6;
  const W = 340;
  let tipStyle = {};
  if (rect) {
    const p = step.placement;
    if (p === "right") {
      tipStyle = { left: rect.right + 18, top: clamp(rect.top + 120, 16, window.innerHeight - 260) };
    } else if (p === "bottom-end") {
      tipStyle = { top: rect.bottom + 16, left: clamp(rect.right - W, 12, window.innerWidth - W - 12) };
    } else {
      tipStyle = { top: rect.bottom + 16, left: clamp(rect.left, 12, window.innerWidth - W - 12) };
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[100]" data-testid="tour-overlay">
      {/* Spotlight */}
      {rect ? (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}
          className="pointer-events-none fixed rounded-xl"
          style={{
            left: rect.left - pad, top: rect.top - pad,
            width: rect.width + pad * 2, height: rect.height + pad * 2,
            boxShadow: "0 0 0 9999px rgba(15,23,42,0.72), 0 0 0 3px #F5A623",
          }}
        />
      ) : (
        <div className="fixed inset-0 bg-slate-900/75" onClick={finish} />
      )}

      {/* Tooltip / card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step.key}
          initial={{ opacity: 0, y: 10, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.25 }}
          className={`fixed bg-white rounded-2xl shadow-2xl p-6 ${rect ? "" : "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"}`}
          style={rect ? { ...tipStyle, width: W } : { width: W }}
          data-testid="tour-card"
        >
          <button onClick={finish} data-testid="tour-skip-btn"
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-700 transition-colors">
            <X className="h-4 w-4" />
          </button>
          {!rect && (
            <div className="h-11 w-11 rounded-xl bg-[#1B2A4A] flex items-center justify-center mb-3">
              <Compass className="h-5 w-5 text-[#F5A623]" />
            </div>
          )}
          <p className="text-[10px] font-semibold uppercase tracking-widest text-[#F5A623] mb-1">
            {t("tour_step")} {i + 1} / {steps.length}
          </p>
          <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: "Work Sans" }}>
            {t(`tour_${step.key}_title`)}
          </h3>
          <p className="text-sm text-gray-500 mt-2 leading-relaxed">{t(`tour_${step.key}_body`)}</p>

          <div className="flex items-center justify-between mt-5">
            <div className="flex gap-1.5">
              {steps.map((_, idx) => (
                <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-5 bg-[#F5A623]" : "w-1.5 bg-gray-200"}`} />
              ))}
            </div>
            <div className="flex items-center gap-2">
              {i > 0 && (
                <button onClick={back} data-testid="tour-back-btn"
                  className="text-sm font-medium text-gray-500 hover:text-gray-800 px-2 transition-colors">
                  {t("tour_back")}
                </button>
              )}
              <button onClick={next} data-testid="tour-next-btn"
                className="text-sm font-semibold bg-[#1B2A4A] hover:bg-[#2a3d63] text-white rounded-full px-4 py-2 transition-colors">
                {i < steps.length - 1 ? t("tour_next") : t("tour_finish")}
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>,
    document.body
  );
}
